import { Input as InputPrimitive } from '@base-ui/react/input';
import type * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * Density options for the Input.
 */
type InputSize = 'md' | 'sm';

const sizeStyles = {
  md: 'h-10 px-3.5 text-base',
  sm: 'h-8 px-2.5 text-sm',
} satisfies Record<InputSize, string>;

const leadingIconPadding = {
  md: 'pl-10',
  sm: 'pl-8',
} satisfies Record<InputSize, string>;

/**
 * Corner radius options for the Input.
 */
type InputRadius = 'md' | 'lg';

const radiusStyles = {
  md: 'rounded-md',
  lg: 'rounded-lg',
} satisfies Record<InputRadius, string>;

/**
 * Frame treatments for the Input. `bare` has no border, radius, background,
 * padding or focus outline, and inherits its font size, so a host surface
 * draws the frame and sets the text size.
 */
type InputVariant = 'default' | 'bare';

const bareStyles = 'rounded-none border-0 bg-transparent p-0 focus:outline-none';

export interface InputProps extends Omit<React.ComponentProps<typeof InputPrimitive>, 'size'> {
  /**
   * Density of the input.
   * @default 'md'
   */
  size?: InputSize;
  /**
   * Corner radius.
   * @default 'md'
   */
  radius?: InputRadius;
  /**
   * Frame treatment. `bare` ignores `size` and `hasLeadingIcon`.
   * @default 'default'
   */
  variant?: InputVariant;
  /**
   * Reserves room at the start of the field for an icon positioned by the caller.
   * @default false
   */
  hasLeadingIcon?: boolean;
  className?: string;
}

export function Input({
  className,
  size = 'md',
  variant = 'default',
  radius = 'md',
  hasLeadingIcon = false,
  ...props
}: InputProps) {
  const isBare = variant === 'bare';
  return (
    <InputPrimitive
      data-size={isBare ? undefined : size}
      data-variant={variant}
      className={cn(
        'w-full',
        'border',
        radiusStyles[radius],
        !isBare && sizeStyles[size],
        !isBare && hasLeadingIcon && leadingIconPadding[size],
        'text-foreground bg-white',
        'border-border-strong',
        'focus:outline-2 focus:-outline-offset-1 focus:outline-solid',
        'focus:outline-ring',
        'placeholder:text-muted-foreground',
        // data-invalid comes from the Base UI Field context; aria-invalid covers standalone use.
        'data-invalid:border-destructive',
        'data-invalid:focus:outline-destructive',
        'aria-invalid:border-destructive',
        'aria-invalid:focus:outline-destructive',
        'data-disabled:border-border-disabled data-disabled:bg-disabled data-disabled:text-text-disabled data-disabled:cursor-not-allowed',
        'disabled:border-border-disabled disabled:bg-disabled disabled:text-text-disabled disabled:cursor-not-allowed',
        isBare && bareStyles,
        className,
      )}
      {...props}
    />
  );
}
